import React, { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import type { Map as LeafletMap } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { onlineIcon, offlineIcon } from './leafletIcons'
import { isPlottable, isVisitorOnline, type MapVisitor } from './visitorMap.helpers'

const WORLD_CENTER: [number, number] = [25, 5]
const WORLD_ZOOM = 2

/** Captures the Leaflet map instance into the parent's ref once ready. */
const MapRefBinder: React.FC<{ onReady: (map: LeafletMap) => void }> = ({ onReady }) => {
  const map = useMap()
  useEffect(() => {
    onReady(map)
  }, [map, onReady])
  return null
}

export interface VisitorMapProps {
  /**
   * The full roster. Plottable visitors are shown: online → red pin,
   * offline → grey pin. Non-plottable ones (no lat/lng) are silently skipped.
   */
  visitors: MapVisitor[]
  /** Fired when the parent should grab the Leaflet map instance for flyTo. */
  onMapReady: (map: LeafletMap) => void
  /** Pin click → open the detail card for that visitor. */
  onSelectVisitor: (visitor: MapVisitor) => void
}

export const VisitorMap: React.FC<VisitorMapProps> = ({
  visitors,
  onMapReady,
  onSelectVisitor,
}) => {
  const plottable = visitors.filter(isPlottable)

  return (
    <MapContainer
      center={WORLD_CENTER}
      zoom={WORLD_ZOOM}
      minZoom={2}
      worldCopyJump
      scrollWheelZoom
      className="vmap-container"
    >
      <MapRefBinder onReady={onMapReady} />
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <MarkerClusterGroup chunkedLoading showCoverageOnHover={false}>
        {plottable.map((v) => (
          <Marker
            key={v.id}
            position={[v.latitude, v.longitude]}
            icon={isVisitorOnline(v) ? onlineIcon : offlineIcon}
            eventHandlers={{ click: () => onSelectVisitor(v) }}
          />
        ))}
      </MarkerClusterGroup>
    </MapContainer>
  )
}
